/**
 * Stripe reports and accepts money in the currency's SMALLEST unit, and how many of those make one
 * major unit is a property of the currency — not a constant 100.
 *
 * This matters the moment a zero-decimal currency is priced. For JPY, `unit_amount: 3000` IS ¥3,000;
 * dividing by 100 renders "¥30" on a card while checkout resolves the same Stripe price and charges
 * ¥3,000. Nothing validates display against charge, so the failure is silent, 100×, and only visible
 * to buyers in that one market.
 *
 * Shared deliberately: before this existed, `orion-web/lib/stripe/client.ts` had the only zero-decimal
 * set in the repo (used for analytics revenue) while the picker, the agent's quotes and the coupon
 * preview each divided by a hardcoded 100. One list in one place is the point — a second copy is how
 * the next currency gets it right in one surface and wrong in three.
 *
 * https://docs.stripe.com/currencies#zero-decimal
 */

/**
 * Currencies whose Stripe API `amount` needs NO division — the amount already IS the major-unit value.
 *
 * ⚠️ This models **Stripe's API amount representation**, which is NOT the same question as "how many
 * decimal places does this currency have". Two currencies are zero-decimal in the real world and still
 * take ×100 amounts, so they must be absent here:
 *
 *   ISK, UGX — "transitioned to a zero-decimal currency, but backward compatibility requires you to
 *              represent it as a two-decimal value, where the decimal amount is always `00`. For
 *              example, to charge 5 UGX, provide an `amount` value of `500`."
 *
 * Also absent, for a DIFFERENT reason: HUF and TWD. Stripe treats those as zero-decimal *for payouts*
 * only — "even though you can charge two-decimal amounts" — so a charge/Price amount in HUF or TWD is
 * an ordinary ×100 value. (An earlier version of this comment lumped HUF in with ISK/UGX; the
 * divisible-by-100 rule it was thinking of applies to manual payouts, not to prices.)
 *
 * Getting any of these four wrong renders their amounts 100× too LOW — the mirror of the JPY bug this
 * module exists to fix, and HUF is a live market.
 *
 * https://docs.stripe.com/currencies#zero-decimal and #special-cases
 */
export const STRIPE_NO_DIVISION_CURRENCIES = new Set(['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'vnd', 'vuv', 'xaf', 'xof', 'xpf']);

/**
 * @deprecated Misleading name — it described the CURRENCY's decimals, but the set is really about how
 * Stripe represents an amount (ISK/UGX are zero-decimal currencies that still take ×100). Kept as an
 * alias so existing imports keep compiling; use `STRIPE_NO_DIVISION_CURRENCIES`.
 */
export const ZERO_DECIMAL_CURRENCIES = STRIPE_NO_DIVISION_CURRENCIES;

/** The divisor taking a Stripe API amount to major units — 1 when Stripe sends whole units, else 100. */
export function minorUnitsPerMajor(currency) {
  return currency && STRIPE_NO_DIVISION_CURRENCIES.has(currency.toLowerCase()) ? 1 : 100;
}

/**
 * A Stripe minor-unit amount → major units for its currency, or `undefined` when there is no amount.
 *
 * `undefined` (never 0) for an absent amount is load-bearing for the callers that render prices: a
 * cadence Stripe could not price must render as "unavailable", because coercing it to 0 shows a free
 * plan while checkout still charges the real price.
 *
 * An UNKNOWN/absent currency is treated as 2-decimal. That is the safe default — every currency we
 * price except JPY is 2-decimal, and it keeps the pre-existing behaviour for the callers that have no
 * currency to hand.
 */
export function minorToMajorUnits(amountMinor, currency) {
  if (amountMinor === null || amountMinor === undefined || amountMinor === '') {
    return undefined;
  }
  const minor = Number(amountMinor);
  if (!Number.isFinite(minor)) {
    return undefined;
  }
  return minor / minorUnitsPerMajor(currency);
}
//# sourceMappingURL=CurrencyMinorUnits.js.map